<script setup lang="ts">
import { computed } from 'vue';
import type { IssueFilters } from '@/composables/useIssues';
import { SEVERITY_LABEL, CATEGORY_LABEL, STATUS_LABEL, SOURCE_LABEL } from './issue-enums';

interface Props {
  modelValue: IssueFilters;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: IssueFilters];
}>();

type TabKey = IssueFilters['tab'];
const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'critical', label: 'Critical' },
  { key: 'mine', label: 'Assigned to me' },
  { key: 'awaiting', label: 'Awaiting response' },
  { key: 'repeats', label: 'Repeat complaints' },
  { key: 'resolved', label: 'Resolved' },
];

function setTab(tab: TabKey) {
  emit('update:modelValue', { ...props.modelValue, tab });
}

function setSearch(e: Event) {
  const val = (e.target as HTMLInputElement).value;
  emit('update:modelValue', { ...props.modelValue, search: val || undefined });
}

const activeFiltersCount = computed(() => {
  let count = 0;
  if (props.modelValue.severity?.length) count += props.modelValue.severity.length;
  if (props.modelValue.category?.length) count += props.modelValue.category.length;
  if (props.modelValue.status?.length) count += props.modelValue.status.length;
  if (props.modelValue.source?.length) count += props.modelValue.source.length;
  if (props.modelValue.aiConfidenceMin) count += 1;
  return count;
});

function clearAll() {
  emit('update:modelValue', {
    tab: props.modelValue.tab,
    search: props.modelValue.search,
  });
}

/** Active filter chip list for display. */
const activeChips = computed(() => {
  const chips: { label: string; remove: () => void }[] = [];
  props.modelValue.severity?.forEach((s) => {
    chips.push({
      label: `Severity: ${SEVERITY_LABEL[s] ?? String(s)}`,
      remove: () => {
        const next = props.modelValue.severity?.filter((v) => v !== s);
        emit('update:modelValue', { ...props.modelValue, severity: next?.length ? next : undefined });
      },
    });
  });
  props.modelValue.category?.forEach((c) => {
    chips.push({
      label: `Category: ${CATEGORY_LABEL[c] ?? String(c)}`,
      remove: () => {
        const next = props.modelValue.category?.filter((v) => v !== c);
        emit('update:modelValue', { ...props.modelValue, category: next?.length ? next : undefined });
      },
    });
  });
  props.modelValue.status?.forEach((s) => {
    chips.push({
      label: `Status: ${STATUS_LABEL[s] ?? String(s)}`,
      remove: () => {
        const next = props.modelValue.status?.filter((v) => v !== s);
        emit('update:modelValue', { ...props.modelValue, status: next?.length ? next : undefined });
      },
    });
  });
  props.modelValue.source?.forEach((s) => {
    chips.push({
      label: `Source: ${SOURCE_LABEL[s] ?? String(s)}`,
      remove: () => {
        const next = props.modelValue.source?.filter((v) => v !== s);
        emit('update:modelValue', { ...props.modelValue, source: next?.length ? next : undefined });
      },
    });
  });
  if (props.modelValue.aiConfidenceMin) {
    chips.push({
      label: `AI confidence ≥${props.modelValue.aiConfidenceMin}%`,
      remove: () => emit('update:modelValue', { ...props.modelValue, aiConfidenceMin: undefined }),
    });
  }
  return chips;
});
</script>

<template>
  <!-- Tab bar -->
  <div class="flex gap-1 overflow-x-auto pb-1 scrollbar-hide" role="tablist" data-testid="issue-tabs">
    <button
      v-for="tab in TABS"
      :key="tab.key"
      role="tab"
      :aria-selected="modelValue.tab === tab.key"
      :class="[
        'flex-shrink-0 px-3 h-8 text-caption font-medium rounded-md transition-colors whitespace-nowrap',
        modelValue.tab === tab.key
          ? 'bg-navy-800 text-white'
          : 'bg-canvas-soft text-muted hover:bg-border hover:text-fg',
      ]"
      @click="setTab(tab.key)"
    >
      {{ tab.label }}
    </button>
  </div>

  <!-- Search + filter row -->
  <div class="flex gap-2 mt-3">
    <div class="relative flex-1 min-w-0">
      <svg
        class="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="search"
        :value="modelValue.search ?? ''"
        placeholder="Search issues…"
        class="w-full h-9 pl-8 pr-3 text-body bg-surface border border-border rounded-md outline-none focus:ring-2 focus:ring-navy-600/20 focus:border-navy-600"
        data-testid="issues-search"
        @input="setSearch"
      >
    </div>
    <button
      v-if="activeFiltersCount > 0"
      class="flex-shrink-0 h-9 px-3 text-caption text-muted hover:text-fg border border-border rounded-md bg-surface hover:bg-canvas-soft transition-colors"
      @click="clearAll"
    >
      Clear filters ({{ activeFiltersCount }})
    </button>
  </div>

  <!-- Active filter chips -->
  <div v-if="activeChips.length > 0" class="flex flex-wrap gap-1.5 mt-2">
    <span
      v-for="(chip, i) in activeChips"
      :key="i"
      class="inline-flex items-center gap-1 px-2 h-6 text-caption bg-navy-100 text-navy-700 border border-navy-100 rounded-pill"
    >
      {{ chip.label }}
      <button
        class="ml-0.5 hover:text-navy-900 transition-colors"
        :aria-label="`Remove filter: ${chip.label}`"
        @click="chip.remove()"
      >
        ×
      </button>
    </span>
  </div>
</template>
